// frontend/src/utils/logger.js
/**
 * Sistema de logging seguro para o frontend
 * Desabilita console.logs em produção para prevenir exposição de dados sensíveis
 */

const isDevelopment = process.env.NODE_ENV === 'development';

export const logger = {
  /**
   * Log para desenvolvimento - NÃO aparece em produção
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Logs de informação - apenas desenvolvimento
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Warnings - apenas desenvolvimento  
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Erros - SEMPRE loga (necessário para debug em produção)
   * Mas sanitiza dados sensíveis
   */
  error: (...args) => {
    // Sanitizar argumentos antes de logar
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.error(...sanitizedArgs);
  },

  /**
   * Debug detalhado - apenas desenvolvimento
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },

  /**
   * Tabelas - apenas desenvolvimento
   */
  table: (data) => {
    if (isDevelopment && console.table) {
      console.table(data);
    }
  },

  /**
   * Grupos - apenas desenvolvimento
   */
  group: (label) => {
    if (isDevelopment && console.group) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDevelopment && console.groupEnd) {
      console.groupEnd();
    }
  }
};

/**
 * Remove dados sensíveis antes de logar
 */
function sanitizeData(data) {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  const sensitiveKeys = [
    'password',
    'token',
    'accessToken',
    'refreshToken',
    'access_token',
    'refresh_token',
    'authorization',
    'api_key',
    'apiKey',
    'secret',
    'secretKey',
    'secret_key',
    'stripe_secret_key',
    'stripe_publishable_key',
    'supabase_service_role_key',
    'jwt',
    'bearer'
  ];

  const sanitized = Array.isArray(data) ? [] : {};

  for (const [key, value] of Object.entries(data)) {
    const keyLower = key.toLowerCase();
    const isSensitive = sensitiveKeys.some(sensitive => keyLower.includes(sensitive));

    if (isSensitive) {
      sanitized[key] = '***REDACTED***';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

// Substituir console global em produção
// Isso silencia TODOS os console.log em produção
if (!isDevelopment) {
  // Salvar referências originais para erros críticos
  const originalError = console.error;
  const originalWarn = console.warn;
  
  // Silenciar logs em produção
  window.console.log = () => {};
  window.console.info = () => {};
  window.console.debug = () => {};
  
  // Manter warnings e erros, mas sanitizados
  window.console.warn = (...args) => {
    // Só mostrar warnings críticos em produção
    const message = args[0]?.toString() || '';
    if (message.includes('Error') || message.includes('Failed') || message.includes('Critical')) {
      originalWarn.apply(console, args);
    }
  };
  
  window.console.error = (...args) => {
    // Sanitizar antes de mostrar
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'string') {
        // Remover tokens e chaves de strings
        return arg
          .replace(/Bearer\s+[A-Za-z0-9\-_.]+/gi, 'Bearer ***REDACTED***')
          .replace(/eyJ[A-Za-z0-9\-_.]+/g, '***JWT_REDACTED***')
          .replace(/sk_[a-zA-Z0-9]+/g, '***STRIPE_KEY***')
          .replace(/whsec_[a-zA-Z0-9]+/g, '***WEBHOOK_SECRET***');
      }
      return arg;
    });
    originalError.apply(console, sanitizedArgs);
  };
}

export default logger;







